/**
 * Minimal dependency-free SVG line chart for the Overview trend (auctions / wins / revenue over
 * the selected date range). No charting library — this monorepo's frontend packages stay
 * dependency-light by convention (see packages/wrapper), and a handful of lines/points is well
 * within what's worth hand-rolling.
 */
const WIDTH = 720;
const HEIGHT = 220;
const PAD = { top: 16, right: 16, bottom: 28, left: 44 };

export default function TrendChart({ data, series }) {
  if (!data || !data.length) return null;

  const innerW = WIDTH - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;

  const allValues = data.flatMap((d) => series.map((s) => Number(d[s.key]) || 0));
  const maxValue = Math.max(1, ...allValues);

  const x = (i) => PAD.left + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
  const y = (v) => PAD.top + innerH - (v / maxValue) * innerH;

  const gridLines = 4;
  const tickEvery = Math.max(1, Math.ceil(data.length / 6));

  return (
    <div className="trend-chart">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" height={HEIGHT} role="img" aria-label="Trend chart">
        {Array.from({ length: gridLines + 1 }).map((_, i) => {
          const gy = PAD.top + (innerH / gridLines) * i;
          const val = Math.round(maxValue - (maxValue / gridLines) * i);
          return (
            <g key={i}>
              <line x1={PAD.left} y1={gy} x2={WIDTH - PAD.right} y2={gy} className="trend-chart__grid" />
              <text x={PAD.left - 8} y={gy + 3} textAnchor="end" className="trend-chart__axis-label">
                {formatCompact(val)}
              </text>
            </g>
          );
        })}

        {data.map((d, i) =>
          i % tickEvery === 0 ? (
            <text key={d.date} x={x(i)} y={HEIGHT - 6} textAnchor="middle" className="trend-chart__axis-label">
              {shortDate(d.date)}
            </text>
          ) : null
        )}

        {series.map((s) => {
          const points = data.map((d, i) => `${x(i)},${y(Number(d[s.key]) || 0)}`).join(' ');
          return <polyline key={s.key} points={points} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />;
        })}

        {series.map((s) =>
          data.map((d, i) => (
            <circle key={`${s.key}-${d.date}`} cx={x(i)} cy={y(Number(d[s.key]) || 0)} r="2.5" fill={s.color}>
              <title>
                {s.label} &middot; {shortDate(d.date)}: {formatCompact(Number(d[s.key]) || 0)}
              </title>
            </circle>
          ))
        )}
      </svg>
      <div className="trend-chart__legend">
        {series.map((s) => (
          <div className="trend-chart__legend-item" key={s.key}>
            <span className="trend-chart__legend-swatch" style={{ background: s.color }} />
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function shortDate(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatCompact(n) {
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
